export interface InjectedObservability {
  /** Cohort input only: never export, log or attach to analytics. */
  seed?: string;
  launchStartEpochMs?: number;
  launchSurface?: boolean;
  appSessionUUID?: string;
}

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const field = (value: Record<string, unknown>, key: string): unknown => {
  try {
    return Object.prototype.hasOwnProperty.call(value, key)
      ? value[key]
      : undefined;
  } catch {
    return undefined;
  }
};

/** Pure parser for the native injected object or injectedObjectJson() result. */
export function parseInjectedObservability(
  input: unknown,
  documentStartEpochMs: number
): InjectedObservability {
  if (typeof input === "string") {
    try {
      input = JSON.parse(input);
    } catch {
      return {};
    }
  }
  if (!record(input)) return {};
  const result: InjectedObservability = {};
  // Session identity is top-level and independent of the versioned observability object.
  const session = field(input, "appSessionUUID");
  if (
    typeof session === "string" &&
    session.length === 36 &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      session
    )
  )
    result.appSessionUUID = session.toLowerCase();

  const observability = field(input, "observability");
  if (!record(observability) || field(observability, "v") !== 1) return result;

  const seed = field(observability, "seed");
  if (
    typeof seed === "string" &&
    seed.length === 32 &&
    /^[0-9a-f]{32}$/.test(seed)
  )
    result.seed = seed;
  const launchSurface = field(observability, "launchSurface");
  if (typeof launchSurface === "boolean") result.launchSurface = launchSurface;
  const launchStart = field(observability, "launchStartEpochMs");
  if (
    typeof launchStart === "number" &&
    Number.isFinite(launchStart) &&
    launchStart > 0 &&
    Number.isFinite(documentStartEpochMs) &&
    documentStartEpochMs >= launchStart &&
    documentStartEpochMs - launchStart <= 120000
  )
    result.launchStartEpochMs = launchStart;
  return result;
}
